import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  X,
  Check,
  Users,
  FileText,
  LogOut,
  BarChart3,
  Building2,
  Ship,
  Layers,
  Search,
  Activity,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import equipoService from '../services/equipoService';
import tipoEquipoService from '../services/tipoEquipoService';
import solicitudService from '../services/solicitudService';
import clientService from '../services/clientService';
import api from '../services/api';
import { authService } from '../services/authService';

function Equipos() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [equipos, setEquipos] = useState([]);
  const [tiposEquipo, setTiposEquipo] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  // Estados de paginación
  const [paginacion, setPaginacion] = useState({
    total: 0,
    skip: 0,
    limit: 25,
    total_pages: 0
  });
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(25);
  
  const [showModalGestion, setShowModalGestion] = useState(false);
  const [showModalEditar, setShowModalEditar] = useState(false);
  const [showModalCrearDirecto, setShowModalCrearDirecto] = useState(false);
  const [equipoAEditar, setEquipoAEditar] = useState(null);
  const [showModalHistorial, setShowModalHistorial] = useState(false);
  const [historialData, setHistorialData] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // Estado para ordenamiento
  const [ordenamiento, setOrdenamiento] = useState({
    campo: null,
    direccion: null // 'asc' o 'desc'
  });

  useEffect(() => {
    loadData();
  }, [filtroEstado, filtroCliente, filtroTipo, busquedaAplicada, paginaActual, registrosPorPagina]);

  // Debounce de la búsqueda: espera 400ms tras dejar de escribir
  useEffect(() => {
    const timer = setTimeout(() => {
      setBusquedaAplicada(busqueda);
      setPaginaActual(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [busqueda]);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const skip = (paginaActual - 1) * registrosPorPagina;
      const limit = registrosPorPagina;
      
      const params = {
        skip: skip,
        limit: limit
      };
      
      if (filtroEstado) {
        params.estado = filtroEstado;
      }
      if (busquedaAplicada.trim()) {
        params.search = busquedaAplicada.trim();
      }
      if (filtroCliente) {
        params.cliente_id = filtroCliente;
      }
      if (filtroTipo) {
        params.tipo_equipo_id = filtroTipo;
      }

      const [equiposResponse, tiposData, clientesData] = await Promise.all([
        equipoService.getEquipos(params),
        tipoEquipoService.getTiposEquipo(),
        clientService.getClientes()
      ]);
      
      // El backend ahora devuelve { equipos: [...], total: X, skip: Y, limit: Z, total_pages: W }
      setEquipos(equiposResponse.equipos || equiposResponse);
      setPaginacion({
        total: equiposResponse.total || 0,
        skip: equiposResponse.skip || 0,
        limit: equiposResponse.limit || limit,
        total_pages: equiposResponse.total_pages || 1
      });
      
      setTiposEquipo(tiposData);
      setClientes(clientesData);
    } catch (err) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarPagina = (nuevaPagina) => {
    if (nuevaPagina >= 1 && nuevaPagina <= paginacion.total_pages) {
      setPaginaActual(nuevaPagina);
    }
  };

  const handleCambiarRegistrosPorPagina = (cantidad) => {
  setRegistrosPorPagina(parseInt(cantidad));
  setPaginaActual(1); // Volver a la primera página al cambiar cantidad
};

  const obtenerRangoMostrado = () => {
    if (paginacion.total === 0) return '0-0 de 0';
    const inicio = paginacion.skip + 1;
    const fin = Math.min(paginacion.skip + equipos.length, paginacion.total);
    return `${inicio}-${fin} de ${paginacion.total}`;
  };

  const generarNumerosPagina = () => {
    const paginas = [];
    const totalPaginas = paginacion.total_pages;
    const actual = paginaActual;
    
    if (totalPaginas <= 7) {
      for (let i = 1; i <= totalPaginas; i++) {
        paginas.push(i);
      }
    } else {
      if (actual <= 4) {
        for (let i = 1; i <= 5; i++) paginas.push(i);
        paginas.push('...');
        paginas.push(totalPaginas);
      } else if (actual >= totalPaginas - 3) {
        paginas.push(1);
        paginas.push('...');
        for (let i = totalPaginas - 4; i <= totalPaginas; i++) paginas.push(i);
      } else {
        paginas.push(1);
        paginas.push('...');
        for (let i = actual - 1; i <= actual + 1; i++) paginas.push(i);
        paginas.push('...');
        paginas.push(totalPaginas);
      }
    }
    
    return paginas;
  };

  // Función para manejar el ordenamiento
  const handleOrdenar = (campo) => {
    setOrdenamiento(prev => {
      if (prev.campo !== campo) {
        return { campo, direccion: 'asc' };
      }
      if (prev.direccion === 'asc') {
        return { campo, direccion: 'desc' };
      }
      if (prev.direccion === 'desc') {
        return { campo: null, direccion: null };
      }
      return { campo, direccion: 'asc' };
    });
  };

  // Obtener equipos ordenados
  const equiposOrdenados = React.useMemo(() => {
    if (!ordenamiento.campo || !ordenamiento.direccion) {
      return equipos;
    }

    return [...equipos].sort((a, b) => {
      let valorA = a[ordenamiento.campo] || '';
      let valorB = b[ordenamiento.campo] || '';

      // Convertir a minúsculas para comparación alfabética
      if (typeof valorA === 'string') valorA = valorA.toLowerCase();
      if (typeof valorB === 'string') valorB = valorB.toLowerCase();

      if (valorA < valorB) return ordenamiento.direccion === 'asc' ? -1 : 1;
      if (valorA > valorB) return ordenamiento.direccion === 'asc' ? 1 : -1;
      return 0;
    });
  }, [equipos, ordenamiento]);

  // Icono de ordenamiento para la columna
  const getIconoOrdenamiento = (campo) => {
    if (ordenamiento.campo !== campo) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    if (ordenamiento.direccion === 'asc') {
      return <ArrowUp size={14} className="text-blue-600" />;
    }
    return <ArrowDown size={14} className="text-blue-600" />;
  };

  const handleEditarEquipo = (equipo) => {
    setEquipoAEditar(equipo);
    setShowModalEditar(true);
  };

  const handleEliminarEquipo = async (equipoId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este equipo? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await equipoService.deleteEquipo(equipoId);
      setSuccess('Equipo eliminado exitosamente');
      loadData();
    } catch (err) {
      setError(err.message || 'Error al eliminar equipo');
    }
  };

  const handleVerHistorial = async (equipoId) => {
    try {
      setLoadingHistorial(true);
      const data = await equipoService.getHistorialEquipo(equipoId);
      setHistorialData(data);
      setShowModalHistorial(true);
    } catch (err) {
      setError(err.message || 'Error al cargar historial');
    } finally {
      setLoadingHistorial(false);
    }
  };

  const handleCerrarHistorial = () => {
    setShowModalHistorial(false);
    setHistorialData(null);
  };

  const getTipoEquipoNombre = (tipoId) => {
    const tipo = tiposEquipo.find(t => t.id === tipoId);
    return tipo ? tipo.nombre : 'N/A';
  };

  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nombre : 'N/A';
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      activo: 'bg-green-100 text-green-800',
      inactivo: 'bg-gray-100 text-gray-800',
      suspendido: 'bg-yellow-100 text-yellow-800'
    };
    return badges[estado] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      let utcDateString = dateString;
      
      if (dateString.includes('T') && !dateString.endsWith('Z') && !dateString.includes('+')) {
        utcDateString = dateString + 'Z';
      }
      
      const date = new Date(utcDateString);
      
      return date.toLocaleString('es', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error al convertir fecha:', error);
      return dateString;
    }
  };

  // Modal para Crear Equipo Directamente (Solo Admin)
  const ModalCrearEquipoDirecto = () => {
    const [numeroSerie, setNumeroSerie] = useState('');
    const [clienteId, setClienteId] = useState('');
    const [tipoEquipoId, setTipoEquipoId] = useState('');
    const [embarcacion, setEmbarcacion] = useState('');
    const [ubicacion, setUbicacion] = useState('');
    const [tarifaPersonalizada, setTarifaPersonalizada] = useState('');
    const [fechaActivacion, setFechaActivacion] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [creando, setCreando] = useState(false);

    const handleCrearEquipo = async () => {
      if (!numeroSerie || !clienteId || !tipoEquipoId || !embarcacion || !fechaActivacion) {
        setError('Complete todos los campos obligatorios');
        return;
      }

      try {
        setCreando(true);
        await api.post('/api/equipos/crear-directo', {
          numero_serie: numeroSerie,
          cliente_id: parseInt(clienteId),
          tipo_equipo_id: parseInt(tipoEquipoId),
          embarcacion,
          ubicacion,
          tarifa_personalizada: tarifaPersonalizada ? parseFloat(tarifaPersonalizada) : null,
          fecha_activacion: new Date(fechaActivacion).toISOString(),
          observaciones
        });

        setShowModalCrearDirecto(false);
        loadData();
        setSuccess('Equipo creado y activado exitosamente');
      } catch (err) {
        setError(err.message || 'Error al crear equipo');
      } finally {
        setCreando(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
            <h2 className="text-xl font-bold text-gray-800">Crear Equipo Directamente</h2>
            <button onClick={() => setShowModalCrearDirecto(false)} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                Este formulario permite crear y activar equipos directamente sin solicitudes.
                Útil para migrar equipos históricos.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Serie / IMEI *
              </label>
              <input
                type="text"
                value={numeroSerie}
                onChange={(e) => setNumeroSerie(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: 123456789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cliente *
              </label>
              <select
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccionar cliente</option>
                {clientes.filter(c => c.activo).map(cliente => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nombre} - {cliente.rfc}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Equipo *
              </label>
              <select
                value={tipoEquipoId}
                onChange={(e) => setTipoEquipoId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccionar tipo</option>
                {tiposEquipo.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nombre} (${tipo.tarifa_mensual}/mes)
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Embarcación *
                </label>
                <input
                  type="text"
                  value={embarcacion}
                  onChange={(e) => setEmbarcacion(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre de la embarcación"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ubicación
                </label>
                <input
                  type="text"
                  value={ubicacion}
                  onChange={(e) => setUbicacion(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ubicación"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Activación *
              </label>
              <input
                type="datetime-local"
                value={fechaActivacion}
                onChange={(e) => setFechaActivacion(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Esta será la fecha desde la cual se empezará a facturar el equipo
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tarifa Personalizada ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={tarifaPersonalizada}
                onChange={(e) => setTarifaPersonalizada(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Dejar vacío para usar tarifa del tipo de equipo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Notas adicionales"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowModalCrearDirecto(false)}
                disabled={creando}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCrearEquipo}
                disabled={creando}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Calendar size={20} />
                {creando ? 'Creando...' : 'Crear y Activar Equipo'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal Unificado de Gestión de Equipos
  const ModalGestionEquipo = () => {
    const [paso, setPaso] = useState(1);
    const [imei, setImei] = useState('');
    const [verificando, setVerificando] = useState(false);
    const [resultadoVerificacion, setResultadoVerificacion] = useState(null);
    const [accionSeleccionada, setAccionSeleccionada] = useState(null);
    
    const [tipoEquipoId, setTipoEquipoId] = useState('');
    const [embarcacion, setEmbarcacion] = useState('');
    const [ubicacion, setUbicacion] = useState('');
    const [tarifaPersonalizada, setTarifaPersonalizada] = useState('');
    const [motivo, setMotivo] = useState('');
    const [nuevoEstado, setNuevoEstado] = useState('');

    const handleVerificarIMEI = async () => {
      if (!imei.trim()) {
        setError('Por favor ingrese un número de serie / IMEI');
        return;
      }

      try {
        setVerificando(true);
        setError('');
        const resultado = await equipoService.verificarEquipo(imei);
        setResultadoVerificacion(resultado);
        
        if (resultado.existe) {
          setEmbarcacion(resultado.equipo.embarcacion || '');
          setUbicacion(resultado.equipo.ubicacion || '');
          setTipoEquipoId(resultado.equipo.tipo_equipo_id || '');
        }
        
        setPaso(2);
      } catch (err) {
        setError(err.message || 'Error al verificar equipo');
      } finally {
        setVerificando(false);
      }
    };

    const handleRegistrarEquipo = async () => {
      if (!tipoEquipoId || !embarcacion || !motivo) {
        setError('Complete todos los campos obligatorios');
        return;
      }

      try {
        await solicitudService.createSolicitudRegistroEquipo({
          numero_serie: imei,
          tipo_equipo_id: parseInt(tipoEquipoId),
          embarcacion,
          ubicacion,
          tarifa_personalizada: tarifaPersonalizada ? parseFloat(tarifaPersonalizada) : null,
          motivo
        });
        setShowModalGestion(false);
        resetModal();
        loadData();
        setSuccess('Solicitud de registro enviada exitosamente');
      } catch (err) {
        setError(err.message || 'Error al enviar solicitud');
      }
    };

    const handleCambiarEstado = async () => {
      if (!nuevoEstado || !motivo) {
        setError('Seleccione el nuevo estado y proporcione un motivo');
        return;
      }

      try {
        await solicitudService.createSolicitudCambioEstado({
          equipo_id: resultadoVerificacion.equipo.id,
          estado_solicitado: nuevoEstado,
          motivo
        });
        setShowModalGestion(false);
        resetModal();
        loadData();
        setSuccess('Solicitud de cambio de estado enviada exitosamente');
      } catch (err) {
        setError(err.message || 'Error al enviar solicitud');
      }
    };

    const handleCambiarEmbarcacion = async () => {
      if (!embarcacion) {
        setError('Ingrese el nombre de la embarcación');
        return;
      }

      try {
        await equipoService.updateEmbarcacion(
          resultadoVerificacion.equipo.id,
          embarcacion,
          ubicacion
        );
        setShowModalGestion(false);
        resetModal();
        loadData();
        setSuccess('Embarcación actualizada exitosamente');
      } catch (err) {
        setError(err.message || 'Error al actualizar embarcación');
      }
    };

    const resetModal = () => {
      setPaso(1);
      setImei('');
      setResultadoVerificacion(null);
      setAccionSeleccionada(null);
      setTipoEquipoId('');
      setEmbarcacion('');
      setUbicacion('');
      setTarifaPersonalizada('');
      setMotivo('');
      setNuevoEstado('');
    };

    const handleCerrarModal = () => {
      setShowModalGestion(false);
      resetModal();
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
            <h2 className="text-xl font-bold text-gray-800">
              {paso === 1 ? 'Gestión de Equipos' : 
               resultadoVerificacion?.existe ? 'Equipo Encontrado' : 'Registrar Nuevo Equipo'}
            </h2>
            <button onClick={handleCerrarModal} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            {/* PASO 1: Ingresar IMEI */}
            {paso === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Serie / IMEI *
                  </label>
                  <input
                    type="text"
                    value={imei}
                    onChange={(e) => setImei(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleVerificarIMEI()}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ingrese el número de serie"
                    autoFocus
                  />
                </div>

                <button
                  onClick={handleVerificarIMEI}
                  disabled={verificando}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Search size={20} />
                  {verificando ? 'Verificando...' : 'Buscar Equipo'}
                </button>
              </div>
            )}

            {/* PASO 2: Resultado de Verificación */}
            {paso === 2 && resultadoVerificacion && (
              <div className="space-y-4">
                {/* EQUIPO NO EXISTE - Formulario de Registro */}
                {!resultadoVerificacion.existe && (
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800">
                        <strong>IMEI:</strong> {imei}
                      </p>
                      <p className="text-yellow-700 text-sm mt-1">
                        Este equipo no está registrado. Complete los datos para solicitar su registro.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Equipo *
                      </label>
                      <select
                        value={tipoEquipoId}
                        onChange={(e) => setTipoEquipoId(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar tipo</option>
                        {tiposEquipo.map(tipo => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nombre} (${tipo.tarifa_mensual})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Embarcación *
                        </label>
                        <input
                          type="text"
                          value={embarcacion}
                          onChange={(e) => setEmbarcacion(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ubicación
                        </label>
                        <input
                          type="text"
                          value={ubicacion}
                          onChange={(e) => setUbicacion(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tarifa Personalizada ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={tarifaPersonalizada}
                        onChange={(e) => setTarifaPersonalizada(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Dejar vacío para usar tarifa del tipo de equipo"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Motivo / Descripción *
                      </label>
                      <textarea
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Explica por qué necesitas registrar este equipo"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleCerrarModal}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleRegistrarEquipo}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                      >
                        Enviar Solicitud de Registro
                      </button>
                    </div>
                  </div>
                )}

                {/* EQUIPO EXISTE - Opciones de Gestión */}
                {resultadoVerificacion.existe && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                      <p className="text-blue-900"><strong>IMEI:</strong> {resultadoVerificacion.equipo.numero_serie}</p>
                      <p className="text-blue-900"><strong>Embarcación:</strong> {resultadoVerificacion.equipo.embarcacion}</p>
                      <p className="text-blue-900"><strong>Tipo:</strong> {resultadoVerificacion.equipo.tipo_equipo}</p>
                      <p className="text-blue-900">
                        <strong>Estado Actual:</strong>{' '}
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(resultadoVerificacion.equipo.estado)}`}>
                          {resultadoVerificacion.equipo.estado}
                        </span>
                      </p>
                    </div>

                    {!accionSeleccionada && (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-gray-700">¿Qué deseas hacer?</p>
                        
                        <button
                          onClick={() => setAccionSeleccionada('cambiar_embarcacion')}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                        >
                          <Ship size={20} />
                          Cambiar Embarcación / Ubicación
                        </button>

                        <button
                          onClick={() => setAccionSeleccionada('cambiar_estado')}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                        >
                          <Activity size={20} />
                          Solicitar Cambio de Estado
                        </button>

                        <button
                          onClick={handleCerrarModal}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancelar
                        </button>
                      </div>
                    )}

                    {/* Formulario Cambiar Embarcación */}
                    {accionSeleccionada === 'cambiar_embarcacion' && (
                      <div className="space-y-4">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <p className="text-sm text-purple-700">
                            Este cambio se realizará inmediatamente sin necesidad de aprobación.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nueva Embarcación *
                          </label>
                          <input
                            type="text"
                            value={embarcacion}
                            onChange={(e) => setEmbarcacion(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nueva Ubicación
                          </label>
                          <input
                            type="text"
                            value={ubicacion}
                            onChange={(e) => setUbicacion(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => setAccionSeleccionada(null)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Volver
                          </button>
                          <button
                            onClick={handleCambiarEmbarcacion}
                            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
                          >
                            Confirmar Cambio
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Formulario Cambiar Estado */}
                    {accionSeleccionada === 'cambiar_estado' && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm text-blue-700">
                            Esta solicitud requiere aprobación del administrador.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nuevo Estado *
                          </label>
                          <select
                            value={nuevoEstado}
                            onChange={(e) => setNuevoEstado(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Seleccionar estado</option>
                            {resultadoVerificacion.estados_disponibles.activo && (
                              <option value="activo">Activo</option>
                            )}
                            {resultadoVerificacion.estados_disponibles.inactivo && (
                              <option value="inactivo">Inactivo</option>
                            )}
                            {resultadoVerificacion.estados_disponibles.suspendido && (
                              <option value="suspendido">Suspendido</option>
                            )}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Motivo *
                          </label>
                          <textarea
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            rows="3"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Explica por qué necesitas cambiar el estado"
                          />
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => setAccionSeleccionada(null)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Volver
                          </button>
                          <button
                            onClick={handleCambiarEstado}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                          >
                            Enviar Solicitud
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Modal de Historial del Equipo
  const ModalHistorialEquipo = () => {
    if (!historialData) return null;

    const getEstadoSolicitudBadge = (estado) => {
      const badges = {
        pendiente: 'bg-yellow-100 text-yellow-800',
        aprobada: 'bg-green-100 text-green-800',
        rechazada: 'bg-red-100 text-red-800'
      };
      return badges[estado] || 'bg-gray-100 text-gray-800';
    };

    const getTipoSolicitudLabel = (tipo) => {
      const labels = {
        registro_equipo: 'Registro de Equipo',
        cambio_estado: 'Cambio de Estado'
      };
      return labels[tipo] || tipo;
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Historial del Equipo</h2>
              <p className="text-sm text-gray-600 mt-1">
                {historialData.equipo.numero_serie} - {historialData.equipo.embarcacion}
              </p>
            </div>
            <button onClick={handleCerrarHistorial} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Información General */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">Información General</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-600">IMEI / Serie</p>
                  <p className="font-medium">{historialData.equipo.numero_serie}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Cliente</p>
                  <p className="font-medium">{historialData.cliente?.nombre || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Tipo de Equipo</p>
                  <p className="font-medium">{historialData.tipo_equipo?.nombre || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Estado Actual</p>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(historialData.equipo.estado)}`}>
                    {historialData.equipo.estado}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Embarcación</p>
                  <p className="font-medium">{historialData.equipo.embarcacion}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Ubicación</p>
                  <p className="font-medium">{historialData.equipo.ubicacion || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Tarifa</p>
                  <p className="font-medium">
                    ${historialData.equipo.tarifa_personalizada 
                      ? historialData.equipo.tarifa_personalizada.toFixed(2)
                      : historialData.tipo_equipo?.tarifa_mensual.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Fecha Instalación</p>
                  <p className="font-medium">
                    {formatDate(historialData.equipo.fecha_instalacion)}
                  </p>
                </div>
              </div>
            </div>

            {/* Solicitudes */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FileText size={20} />
                Solicitudes ({historialData.solicitudes.length})
              </h3>
              {historialData.solicitudes.length === 0 ? (
                <p className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                  No hay solicitudes registradas
                </p>
              ) : (
                <div className="space-y-3">
                  {historialData.solicitudes.map((solicitud) => (
                    <div key={solicitud.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="font-semibold text-gray-800">
                            {getTipoSolicitudLabel(solicitud.tipo_solicitud)}
                          </span>
                          {solicitud.estado_solicitado && (
                            <span className="ml-2 text-sm text-gray-600">
                              → {solicitud.estado_solicitado}
                            </span>
                          )}
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoSolicitudBadge(solicitud.estado_solicitud)}`}>
                          {solicitud.estado_solicitud}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                        <div>
                          <p className="text-xs text-gray-600">Solicitante</p>
                          <p className="font-medium">{solicitud.usuario_solicitante}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Fecha Solicitud</p>
                          <p className="font-medium">
                            {formatDate(solicitud.fecha_solicitud)}
                          </p>
                        </div>
                        {solicitud.usuario_revisor && (
                          <>
                            <div>
                              <p className="text-xs text-gray-600">Revisor</p>
                              <p className="font-medium">{solicitud.usuario_revisor}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Fecha Revisión</p>
                              <p className="font-medium">
                                {formatDate(solicitud.fecha_revision)}
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      {solicitud.motivo && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-600">Motivo</p>
                          <p className="text-sm">{solicitud.motivo}</p>
                        </div>
                      )}

                      {solicitud.comentario_revision && (
                        <div className="mt-2 bg-blue-50 rounded p-2">
                          <p className="text-xs text-gray-600">Comentario del Revisor</p>
                          <p className="text-sm text-blue-900">{solicitud.comentario_revision}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Historial de Cambios de Estado */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Activity size={20} />
                Cambios de Estado ({historialData.historial_cambios.length})
              </h3>
              {historialData.historial_cambios.length === 0 ? (
                <p className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                  No hay cambios de estado registrados
                </p>
              ) : (
                <div className="space-y-2">
                  {historialData.historial_cambios.map((cambio) => (
                    <div key={cambio.id} className="flex items-start gap-3 border-l-4 border-blue-500 pl-4 py-2 hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {cambio.estado_anterior && (
                            <>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(cambio.estado_anterior)}`}>
                                {cambio.estado_anterior}
                              </span>
                              <span className="text-gray-400">→</span>
                            </>
                          )}
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(cambio.estado_nuevo)}`}>
                            {cambio.estado_nuevo}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{cambio.motivo}</p>
                        <div className="flex gap-4 mt-1 text-xs text-gray-500">
                          <span>Por: {cambio.usuario}</span>
                          <span>{formatDate(cambio.fecha)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botón Cerrar */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={handleCerrarHistorial}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal de Edición de Equipo
  const ModalEditarEquipo = () => {
    const [embarcacion, setEmbarcacion] = useState(equipoAEditar?.embarcacion || '');
    const [ubicacion, setUbicacion] = useState(equipoAEditar?.ubicacion || '');
    const [tipoEquipoId, setTipoEquipoId] = useState(equipoAEditar?.tipo_equipo_id || '');
    const [tarifaPersonalizada, setTarifaPersonalizada] = useState(equipoAEditar?.tarifa_personalizada || '');
    const [observaciones, setObservaciones] = useState(equipoAEditar?.observaciones || '');

    const handleGuardarCambios = async () => {
      if (!embarcacion || !tipoEquipoId) {
        setError('Complete los campos obligatorios');
        return;
      }

      try {
        await equipoService.updateEquipo(equipoAEditar.id, {
          numero_serie: equipoAEditar.numero_serie,
          tipo_equipo_id: parseInt(tipoEquipoId),
          embarcacion,
          ubicacion,
          tarifa_personalizada: tarifaPersonalizada ? parseFloat(tarifaPersonalizada) : null,
          observaciones
        });
        setShowModalEditar(false);
        setEquipoAEditar(null);
        loadData();
        setSuccess('Equipo actualizado exitosamente');
      } catch (err) {
        setError(err.message || 'Error al actualizar equipo');
      }
    };

    const handleCerrar = () => {
      setShowModalEditar(false);
      setEquipoAEditar(null);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
            <h2 className="text-xl font-bold text-gray-800">Editar Equipo</h2>
            <button onClick={handleCerrar} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-900"><strong>IMEI:</strong> {equipoAEditar?.numero_serie}</p>
              <p className="text-sm text-blue-700 mt-1">Este campo no se puede modificar</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Equipo *
              </label>
              <select
                value={tipoEquipoId}
                onChange={(e) => setTipoEquipoId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccionar tipo</option>
                {tiposEquipo.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nombre} (${tipo.tarifa_mensual})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Embarcación *
                </label>
                <input
                  type="text"
                  value={embarcacion}
                  onChange={(e) => setEmbarcacion(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ubicación
                </label>
                <input
                  type="text"
                  value={ubicacion}
                  onChange={(e) => setUbicacion(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tarifa Personalizada ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={tarifaPersonalizada}
                onChange={(e) => setTarifaPersonalizada(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Dejar vacío para usar tarifa del tipo de equipo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Notas adicionales sobre el equipo"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleCerrar}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardarCambios}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-slate-900 to-blue-900 text-white">
        <div className="p-6 border-b border-blue-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <Ship size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MarineMonitor</h1>
              <p className="text-xs text-blue-200">Multi-Plataforma</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <BarChart3 size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/equipos" className="flex items-center gap-3 px-4 py-3 bg-blue-800 text-white rounded-lg">
            <Package size={20} />
            <span>Equipos</span>
          </Link>
          <Link to="/tipos-equipo" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <Layers size={20} />
            <span>Tipos de Equipo</span>
          </Link>
          {user?.rol === 'admin' && (
            <Link to="/clientes" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
              <Building2 size={20} />
              <span>Clientes</span>
            </Link>
          )}
          {user?.rol === 'admin' && (
            <Link to="/users" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
              <Users size={20} />
              <span>Usuarios</span>
            </Link>
          )}
          <Link to="/solicitudes" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <FileText size={20} />
            <span>Solicitudes</span>
          </Link>
          <Link to="/informes" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <FileText size={20} />
            <span>Informes</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs text-blue-200">{user?.rol}</p>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-blue-800 rounded-lg transition" title="Cerrar Sesión">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white shadow-sm p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Gestión de Equipos</h1>
              <p className="text-gray-600 mt-1">Administra los equipos de rastreo</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowModalGestion(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Search size={20} />
                Gestionar Equipo
              </button>
              {user?.rol === 'admin' && (
                <button
                  onClick={() => setShowModalCrearDirecto(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <Plus size={20} />
                  Crear Equipo Directo
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-red-700 text-sm">{error}</p>
                <button onClick={() => setError('')} className="text-red-600 text-xs underline mt-1">
                  Cerrar
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Check className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-green-700 text-sm">{success}</p>
                <button onClick={() => setSuccess('')} className="text-green-600 text-xs underline mt-1">
                  Cerrar
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow mb-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Búsqueda */}
              <div className={user?.rol === 'admin' ? 'lg:col-span-1' : 'lg:col-span-2'}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="N° de serie o embarcación..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Filtro por Estado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  value={filtroEstado}
                  onChange={(e) => { setFiltroEstado(e.target.value); setPaginaActual(1); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todos</option>
                  <option value="activo">Activos</option>
                  <option value="inactivo">Inactivos</option>
                  <option value="suspendido">Suspendidos</option>
                </select>
              </div>

              {/* Filtro por Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Equipo
                </label>
                <select
                  value={filtroTipo}
                  onChange={(e) => { setFiltroTipo(e.target.value); setPaginaActual(1); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todos</option>
                  {tiposEquipo.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Filtro por Cliente (solo admin) */}
              {user?.rol === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cliente
                  </label>
                  <select
                    value={filtroCliente}
                    onChange={(e) => { setFiltroCliente(e.target.value); setPaginaActual(1); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos</option>
                    {clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>{cliente.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Cargando equipos...</div>
            ) : equipos.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No hay equipos registrados
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleOrdenar('numero_serie')}
                        >
                          <div className="flex items-center gap-1">
                            Número de Serie
                            {getIconoOrdenamiento('numero_serie')}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Embarcación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cliente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ubicación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha Activación
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {equiposOrdenados.map((equipo) => (
                        <tr key={equipo.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {equipo.numero_serie}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {equipo.embarcacion}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getClienteNombre(equipo.cliente_id)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getTipoEquipoNombre(equipo.tipo_equipo_id)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(equipo.estado)}`}>
                              {equipo.estado}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {equipo.ubicacion || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(equipo.ultima_solicitud_activacion)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleVerHistorial(equipo.id)}
                              className="text-purple-600 hover:text-purple-900 inline-flex items-center"
                              title="Ver Historial"
                            >
                              <FileText size={18} />
                            </button>
                            {user?.rol === 'admin' && (
                              <>
                                <button
                                  onClick={() => handleEditarEquipo(equipo)}
                                  className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                                  title="Editar"
                                >
                                  <Edit size={18} />
                                </button>
                                <button
                                  onClick={() => handleEliminarEquipo(equipo.id)}
                                  className="text-red-600 hover:text-red-900 inline-flex items-center"
                                  title="Eliminar"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Controles de Paginación */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    {/* Información de registros */}
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{obtenerRangoMostrado()}</span> equipos
                      </p>
                      
                      {/* Selector de cantidad por página */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700">Mostrar:</label>
                        <select
                          value={registrosPorPagina}
                          onChange={(e) => handleCambiarRegistrosPorPagina(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={500}>500</option>
                        </select>
                      </div>
                    </div>

                    {/* Botones de navegación */}
                    {paginacion.total_pages > 1 && (
                      <div className="flex items-center gap-2">
                        {/* Primera página */}
                        <button
                          onClick={() => handleCambiarPagina(1)}
                          disabled={paginaActual === 1}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Primera página"
                        >
                          <ChevronsLeft size={18} />
                        </button>

                        {/* Página anterior */}
                        <button
                          onClick={() => handleCambiarPagina(paginaActual - 1)}
                          disabled={paginaActual === 1}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Anterior"
                        >
                          <ChevronLeft size={18} />
                        </button>

                        {/* Números de página */}
                        <div className="flex items-center gap-1">
                          {generarNumerosPagina().map((num, idx) => (
                            num === '...' ? (
                              <span key={`dots-${idx}`} className="px-3 py-1 text-gray-500">...</span>
                            ) : (
                              <button
                                key={num}
                                onClick={() => handleCambiarPagina(num)}
                                className={`px-3 py-1 rounded-lg border ${
                                  paginaActual === num
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                {num}
                              </button>
                            )
                          ))}
                        </div>

                        {/* Página siguiente */}
                        <button
                          onClick={() => handleCambiarPagina(paginaActual + 1)}
                          disabled={paginaActual === paginacion.total_pages}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Siguiente"
                        >
                          <ChevronRight size={18} />
                        </button>

                        {/* Última página */}
                        <button
                          onClick={() => handleCambiarPagina(paginacion.total_pages)}
                          disabled={paginaActual === paginacion.total_pages}
                          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Última página"
                        >
                          <ChevronsRight size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showModalGestion && <ModalGestionEquipo />}
      {showModalEditar && equipoAEditar && <ModalEditarEquipo />}
      {showModalCrearDirecto && <ModalCrearEquipoDirecto />}
      {showModalHistorial && historialData && <ModalHistorialEquipo />}
    </div>
  );
}

export default Equipos;